import { useId, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "@tanstack/react-router";
import { browserSupportsWebAuthn } from "@simplewebauthn/browser";
import { Dialog } from "@/components/ui/Dialog";
import { Alert } from "@/components/ui/Alert";
import { ApiErrorBanner } from "@/components/ui/ApiErrorBanner";
import { ApiError, isStaleSessionError } from "@/lib/api/client";
import { useAuth } from "@/providers/auth-provider";
import { deletionBlockReasons, getAccount } from "../api/account.api";
import { isStepUpRequired } from "../api/stepup.api";
import { accountKeys } from "../hooks/useAccount";
import { useRequestAccountDeletion } from "../hooks/useAccountDeletion";
import { usePasskeys } from "../hooks/usePasskeys";
import { useStepUp } from "../hooks/useStepUp";
import { useCeremonyOwner } from "@/hooks/useCeremonyOwner";
import { StaleSessionNotice } from "./StaleSessionNotice";
import { StepUpDialog } from "./StepUpDialog";

interface DeleteAccountDialogProps {
  open: boolean;
  onClose: () => void;
  withinMfaGate?: boolean;
}

const inputClass =
  "h-11 w-full rounded-lg border border-border bg-surface px-3.5 text-sm text-heading transition-colors placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-indigo/40 focus:border-indigo disabled:opacity-50 aria-disabled:opacity-50 hover:border-border-light";

export function DeleteAccountDialog({
  open,
  onClose,
  withinMfaGate = false,
}: DeleteAccountDialogProps) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { data: passkeyData } = usePasskeys();
  const accountEmail = useQuery({
    queryKey: accountKeys.me(),
    queryFn: getAccount,
    enabled: false,
  }).data?.account.email;
  const request = useRequestAccountDeletion();
  const ceremonyOwner = useCeremonyOwner();
  const stepUp = useStepUp(ceremonyOwner);
  const [password, setPassword] = useState("");
  const warningId = useId();
  const confirmBodyId = useId();

  const supportsWebAuthn = browserSupportsWebAuthn();
  const mfaEnabled = passkeyData?.mfaEnabled ?? false;
  const hasPasskey = (passkeyData?.passkeys.length ?? 0) > 0;
  const hasTotp = passkeyData?.totpEnabled ?? false;

  const scheduledFor = request.data?.scheduledFor ?? null;
  const error = request.error && !isStepUpRequired(request.error) ? request.error : null;
  const blockedReasons = deletionBlockReasons(error);
  const staleSession = isStaleSessionError(error);
  const passwordFieldError =
    error instanceof ApiError && error.status === 400
      ? error.errors?.find((fieldError) => fieldError.field === "password")?.message
      : undefined;

  const signOut = () => {
    logout();
    navigate({ to: "/login" });
  };

  const close = () => {
    if (request.isPending || stepUp.busy) return;
    if (scheduledFor !== null) {
      signOut();
      return;
    }
    setPassword("");
    request.reset();
    stepUp.cancel();
    onClose();
  };

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!password || request.isPending) return;
    void stepUp.run(
      (proof) => request.mutateAsync({ password, stepUp: proof }).then(() => undefined),
      {
        needsProof: mfaEnabled,
        dashboardOnly: true,
        onDismiss: request.reset,
      },
    );
  };

  const destination = (to: "/apps" | "/billing", label: string) =>
    withinMfaGate ? (
      <span className="font-medium text-heading">{label}</span>
    ) : (
      <Link to={to} className="font-medium text-indigo hover:text-indigo-bright">
        {label}
      </Link>
    );

  const dateLabel =
    scheduledFor !== null
      ? new Date(scheduledFor).toLocaleDateString(i18n.language, { timeZone: "UTC" })
      : "";

  return (
    <>
      <Dialog
        open={open}
        onClose={close}
        title={
          scheduledFor !== null
            ? t("account.deletion.scheduledTitle")
            : t("account.deletion.confirmTitle")
        }
        describedBy={scheduledFor === null ? `${warningId} ${confirmBodyId}` : undefined}
      >
        {scheduledFor !== null ? (
          <div className="space-y-4">
            <Alert variant="success">
              {t("account.deletion.scheduledBody", { date: dateLabel })}
            </Alert>
            <button
              type="button"
              onClick={signOut}
              autoFocus
              data-autofocus
              className="w-full rounded-lg bg-indigo px-4 py-2.5 text-sm font-semibold text-white transition-all hover:bg-indigo-bright"
            >
              {t("nav.logout")}
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            {open && accountEmail && (
              <input
                id="delete-account-username"
                type="text"
                name="username"
                autoComplete="username"
                value={accountEmail}
                readOnly
                hidden
              />
            )}
            <Alert id={warningId} variant="warning">
              {t("account.deletion.warning")}
            </Alert>
            <p id={confirmBodyId} className="text-sm text-muted">
              {t("account.deletion.confirmBody")}
            </p>

            <div className="flex flex-col gap-1.5">
              <label htmlFor="delete-account-password" className="text-sm font-medium text-subtle">
                {t("account.deletion.passwordLabel")}
              </label>
              <input
                id="delete-account-password"
                name="password"
                type="password"
                autoComplete="current-password"
                data-autofocus={staleSession || request.isPending ? undefined : true}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                readOnly={request.isPending}
                aria-disabled={request.isPending || undefined}
                aria-invalid={passwordFieldError ? true : undefined}
                aria-describedby={passwordFieldError ? "delete-account-password-error" : undefined}
                className={inputClass}
              />
              {passwordFieldError && (
                <p id="delete-account-password-error" role="alert" className="text-xs text-danger">
                  {passwordFieldError}
                </p>
              )}
            </div>

            {blockedReasons && (
              <div
                role="alert"
                className="rounded-lg border border-warning/30 bg-warning/10 p-3 text-xs"
              >
                <p className="font-medium text-heading">{t("account.deletion.blockedTitle")}</p>
                <ul className="mt-2 list-disc space-y-1 pl-4 text-muted">
                  {blockedReasons.includes("apps") && (
                    <li>
                      {t("account.deletion.reasonApps")} {destination("/apps", t("nav.apps"))}.
                    </li>
                  )}
                  {blockedReasons.includes("subscription") && (
                    <li>
                      {t("account.deletion.reasonSubscription")}{" "}
                      {destination("/billing", t("nav.billing"))}.
                    </li>
                  )}
                  {blockedReasons.includes("pending_transfer") && (
                    <li>
                      {t("account.deletion.reasonPendingTransfer")}{" "}
                      {destination("/apps", t("nav.apps"))}.
                    </li>
                  )}
                </ul>
                {withinMfaGate && (
                  <p className="mt-2 text-muted">{t("account.deletion.blockedBehindMfaGate")}</p>
                )}
              </div>
            )}
            {error && !blockedReasons && !staleSession && !passwordFieldError && (
              <ApiErrorBanner error={error} />
            )}
            {staleSession && (
              <StaleSessionNotice
                returnTo="/account"
                body={t("account.staleSession.deletionBody")}
                autoFocus
              />
            )}
            {stepUp.dismissedRequired && !request.isPending && (
              <p role="alert" className="text-xs text-danger">
                {t("account.stepUp.dismissedIncomplete")}
              </p>
            )}

            <div className="flex justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={close}
                disabled={request.isPending}
                className="rounded-lg border border-border bg-surface px-4 py-2.5 text-sm font-medium text-muted transition-colors hover:border-border-light hover:text-heading disabled:opacity-50"
              >
                {t("common.cancel")}
              </button>
              <button
                type="submit"
                disabled={!request.isPending && (!password || staleSession)}
                aria-disabled={request.isPending || undefined}
                aria-busy={request.isPending || undefined}
                data-autofocus={request.isPending ? true : undefined}
                className="rounded-lg bg-danger px-4 py-2.5 text-sm font-semibold text-white transition-all hover:bg-danger/90 disabled:opacity-50 disabled:cursor-not-allowed aria-disabled:opacity-50 aria-disabled:cursor-not-allowed"
              >
                {request.isPending
                  ? t("account.deletion.submitting")
                  : t("account.deletion.submit")}
              </button>
            </div>
          </form>
        )}
      </Dialog>

      <StepUpDialog
        key={stepUp.nonce}
        open={stepUp.open}
        busy={stepUp.busy}
        error={stepUp.error}
        proofOnWire={stepUp.proofOnWire}
        actionNeedsDashboardSession={stepUp.dashboardOnly}
        serverDemandedProof={stepUp.serverDemandedProof}
        supportsWebAuthn={supportsWebAuthn}
        hasPasskey={hasPasskey}
        hasTotp={hasTotp}
        onPasskey={stepUp.withPasskey}
        onTotp={stepUp.withTotp}
        onCancel={stepUp.cancel}
        ceremonyOwner={ceremonyOwner}
        subtitle={t("account.stepUp.deletionSubtitle")}
        staleSessionBody={t("account.staleSession.deletionBody")}
        returnTo="/account"
      />
    </>
  );
}
